// src/components/NotificationsScreen.jsx
// صفحة الإشعارات — تعرض جميع الإشعارات والرسائل المرتبطة بالحساب
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import { NotificationBadge, useUnreadRequests } from './NotificationBadge';
import {
  FiBell, FiCheck, FiTrash2, FiMessageSquare, FiInfo,
  FiDollarSign, FiShield, FiRefreshCw, FiInbox, FiUser
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';

// تنسيق الوقت النسبي بالعربية (بسيط بدون مكتبة locale)
const formatRelativeTime = (date) => {
  if (!date) return '';
  const now = new Date();
  const diff = Math.floor((now - date) / 1000); // الفرق بالثواني
  if (diff < 60) return 'الآن';
  if (diff < 3600) return `منذ ${Math.floor(diff / 60)} دقيقة`;
  if (diff < 86400) return `منذ ${Math.floor(diff / 3600)} ساعة`;
  if (diff < 604800) return `منذ ${Math.floor(diff / 86400)} يوم`;
  return date.toLocaleDateString('ar-DZ');
};

const NotificationIcon = ({ type }) => {
  const iconClas = "w-5 h-5";
  switch (type) {
    case 'message': return <FiMessageSquare className={`${iconClas} text-blue-500`} />;
    case 'payment': return <FiDollarSign className={`${iconClas} text-green-500`} />;
    case 'security': return <FiShield className={`${iconClas} text-red-500`} />;
    default: return <FiInfo className={`${iconClas} text-purple-500`} />;
  }
};

const NotificationsScreen = ({ userType: userTypeProp = 'client' }) => {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [markingRead, setMarkingRead] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const effectiveUserType = userType || userTypeProp;

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await supabaseService.getNotificationsForUser(user.id);
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.isRead).length);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // 🆕 استماعRealtime للإشعارات الجديدة
  useEffect(() => {
    if (!user?.id) return;

    // استماع للإشعارات الجديدة
    const channel = supabaseService.listenToNewNotifications(user.id, (newNotification) => {
      setNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => prev + (newNotification.isRead ? 0 : 1));
    });

    return () => {
      try { channel.unsubscribe(); } catch (e) { }
    };
  }, [user?.id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadNotifications();
    setRefreshing(false);
    toast.success('تم تحديث الإشعارات', { duration: 1500 });
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id || notifications.length === 0) return;
    setMarkingRead(true);
    try {
      await supabaseService.markAllNotificationsAsRead(user.id);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      toast.success('تم تعييم جميع الإشعارات كمقروءة', { duration: 2000 });
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('فشل تحديث الإشعارات');
    } finally {
      setMarkingRead(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!user?.id || notifications.length === 0) return;
    if (!window.confirm('هل أنت متأكد من رغبتك في حذف جميع الإشعارات؟ لا يمكن التراجع.')) return;
    setDeleting(true);
    try {
      await supabaseService.deleteAllNotifications(user.id);
      setNotifications([]);
      setUnreadCount(0);
      toast.success('تم حذف جميع الإشعارات', { duration: 2000 });
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      toast.error('فشل حذف الإشعارات');
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await supabaseService.markNotificationAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await supabaseService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (!notifications.find(n => n.id === notificationId)?.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('فشل حذف الإشعار');
    }
  };

  const handleNotificationClick = (notification) => {
    // تعييم كمقروء عند النقر
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
    // إذا كانت الإشعار ذو صلة بطلب، انتقل لصفحة الطلبات
    if (notification.relatedRequestId || notification.request_id) {
      if (effectiveUserType === 'provider') {
        navigate('/provider', { state: { tab: 'requests' } });
      } else {
        navigate('/client', { state: { tab: 'requests' } });
      }
    }
  };

  if (loading) {
    return (
      <div className="p-4 space-y-4" dir="rtl">
        <div className="h-12 bg-gray-200 rounded-xl animate-pulse" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-20 bg-gray-200 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="pb-20 md:pb-0 min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <FiBell className="w-6 h-6 text-gray-700" />
            {unreadCount > 0 && <NotificationBadge count={unreadCount} />}
          </div>
          <h1 className="text-xl font-bold text-gray-900">الإشعارات</h1>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition"
          title="تحديث"
        >
          <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Subheader actions */}
      {notifications.length > 0 && (
        <div className="bg-white border-b border-gray-100 px-4 py-2 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {unreadCount > 0
              ? `${unreadCount} غير مقروءة من أصل ${notifications.length}`
              : `كل الإشعارات (${notifications.length})`}
          </span>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={markingRead}
                className="text-xs text-blue-600 font-medium hover:text-blue-800 disabled:opacity-50"
              >
                تعيين الكل كمقروء
              </button>
            )}
            <button
              onClick={handleDeleteAll}
              disabled={deleting}
              className="text-xs text-red-600 font-medium hover:text-red-800 disabled:opacity-50"
            >
              حذف الكل
            </button>
          </div>
        </div>
      )}

      {/* Notifications list */}
      <div className="p-2">
        {notifications.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FiInbox className="w-10 h-10 text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-700 mb-1">لا توجد إشعارات</h3>
            <p className="text-sm text-gray-400">ستظهر لك الإشعارات الجديدة هنا عندما تصلك.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`p-3 rounded-xl cursor-pointer transition-all border ${
                  notification.isRead
                    ? 'bg-white border-gray-200 hover:bg-gray-50'
                    : 'bg-blue-50 border-blue-100 hover:bg-blue-100'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {notification.title === 'رسالة جديدة' ? (
                      <FiMessageSquare className="w-5 h-5 text-blue-500" />
                    ) : (
                      <NotificationIcon type={notification.data?.type || 'info'} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold ${
                      notification.isRead ? 'text-gray-700' : 'text-gray-900'
                    }`}>
                      {notification.title || 'إشعار جديد'}
                    </p>
                    <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                      {notification.message || ''}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatRelativeTime(notification.createdAt)}
                    </p>
                  </div>
                  {!notification.isRead && (
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-1" />
                  )}
                  <div className="flex-shrink-0 flex items-center gap-1">
                    {!notification.isRead && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                        }}
                        className="p-1 rounded hover:bg-gray-200 transition"
                        title="تعيين كمقروء"
                      >
                        <FiCheck className="w-3.5 h-3.5 text-gray-500" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(notification.id);
                      }}
                      className="p-1 rounded hover:bg-gray-200 transition"
                      title="حذف"
                    >
                      <FiTrash2 className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsScreen;
